import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Check, FileText, User, Plus, Download } from 'lucide-react';

const STEPS = [
  { id: 'client', duration: 2200 },
  { id: 'items', duration: 2800 },
  { id: 'generate', duration: 2000 },
  { id: 'done', duration: 2000 },
];

const TOTAL_CYCLE = STEPS.reduce((sum, s) => sum + s.duration, 0);

const InvoiceDemo = () => {
  const [step, setStep] = useState(0);
  const [itemsVisible, setItemsVisible] = useState(0);

  useEffect(() => {
    let timeout;
    const runStep = (currentStep) => {
      setStep(currentStep);
      if (currentStep === 1) {
        setItemsVisible(0);
        setTimeout(() => setItemsVisible(1), 600);
        setTimeout(() => setItemsVisible(2), 1200);
        setTimeout(() => setItemsVisible(3), 1800);
      }
      timeout = setTimeout(() => {
        const next = (currentStep + 1) % STEPS.length;
        if (next === 0) {
          setItemsVisible(0);
        }
        runStep(next);
      }, STEPS[currentStep].duration);
    };
    runStep(0);
    return () => clearTimeout(timeout);
  }, []);

  const progressWidth = () => {
    const stepPercents = [25, 50, 85, 100];
    return stepPercents[step] || 0;
  };

  return (
    <div className="relative w-full" data-testid="hero-invoice-demo">
      {/* Fake browser window */}
      <div className="bg-white rounded-xl shadow-2xl border border-zinc-200 overflow-hidden">
        {/* Browser bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-zinc-100 border-b border-zinc-200">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="bg-white rounded px-3 py-1 text-[10px] text-zinc-400 border border-zinc-200 w-48 text-center truncate">
              app.xyron-erp.ch/factures/new
            </div>
          </div>
        </div>

        {/* App content */}
        <div className="p-5 min-h-[320px] relative">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-red-600" />
              <span className="font-semibold text-sm text-zinc-900">Nouvelle facture</span>
            </div>
            <span className="text-[10px] text-zinc-400 font-mono">FAC-2026-0042</span>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-zinc-100 rounded-full mb-5 overflow-hidden">
            <motion.div
              className="h-full bg-red-500 rounded-full"
              animate={{ width: `${progressWidth()}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>

          {/* Step 1: Select Client */}
          <div className="mb-4">
            <div className="text-[10px] text-zinc-500 mb-1.5 font-medium uppercase tracking-wider">Client</div>
            <AnimatePresence mode="wait">
              {step === 0 ? (
                <motion.div
                  key="selecting"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="relative"
                >
                  <div className="border border-red-300 bg-red-50/50 rounded-md p-2.5 flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-red-400" />
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 1] }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                      className="text-xs text-zinc-900 font-medium"
                    >
                      BN Consulting GmbH
                    </motion.span>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.2, type: "spring" }}
                      className="ml-auto"
                    >
                      <Check className="w-3.5 h-3.5 text-green-500" />
                    </motion.div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="selected"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border border-zinc-200 rounded-md p-2.5 flex items-center gap-2 bg-zinc-50"
                >
                  <User className="w-3.5 h-3.5 text-zinc-400" />
                  <span className="text-xs text-zinc-700 font-medium">BN Consulting GmbH</span>
                  <Check className="w-3.5 h-3.5 text-green-500 ml-auto" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Step 2: Invoice Items */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider">Articles</span>
              {step >= 1 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-1 text-[10px] text-red-600 font-medium"
                >
                  <Plus className="w-3 h-3" />
                  Ajouter
                </motion.div>
              )}
            </div>
            <div className="space-y-1.5">
              {[
                { name: "Dev Web - Forfait", qty: "1", price: "2'400.00" },
                { name: "Maintenance mensuelle", qty: "3", price: "450.00" },
                { name: "Hebergement Cloud", qty: "12", price: "29.90" },
              ].map((item, i) => (
                <AnimatePresence key={i}>
                  {(step >= 2 || (step === 1 && itemsVisible > i)) && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, height: 0 }}
                      animate={{ opacity: 1, y: 0, height: "auto" }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center gap-3 text-xs border border-zinc-100 rounded px-3 py-2 bg-white"
                    >
                      <span className="text-zinc-700 flex-1 truncate">{item.name}</span>
                      <span className="text-zinc-400 w-6 text-right">x{item.qty}</span>
                      <span className="text-zinc-900 font-mono font-medium w-20 text-right">{item.price}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              ))}
            </div>
          </div>

          {/* Totals - visible from step 2 */}
          <AnimatePresence>
            {step >= 2 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-zinc-200 pt-3 mt-3"
              >
                <div className="flex justify-between text-xs text-zinc-500 mb-1">
                  <span>Sous-total</span>
                  <span className="font-mono">CHF 4'008.80</span>
                </div>
                <div className="flex justify-between text-xs text-zinc-500 mb-1">
                  <span>TVA 8.1%</span>
                  <span className="font-mono">CHF 324.71</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-zinc-900 mt-2">
                  <span>Total TTC</span>
                  <span className="font-mono">CHF 4'333.51</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step 3: Generate PDF */}
          <AnimatePresence>
            {step >= 2 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-4"
              >
                {step === 3 ? (
                  <motion.div
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    className="flex items-center justify-center gap-2 bg-green-500 text-white rounded-md py-2.5 text-xs font-semibold"
                  >
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      <Check className="w-4 h-4" />
                    </motion.div>
                    PDF genere avec succes !
                  </motion.div>
                ) : (
                  <motion.div
                    className="flex items-center justify-center gap-2 bg-red-600 text-white rounded-md py-2.5 text-xs font-semibold cursor-default"
                    animate={step === 2 ? { scale: [1, 1.02, 1] } : {}}
                    transition={{ duration: 0.5, delay: 0.8 }}
                  >
                    <Download className="w-3.5 h-3.5" />
                    Generer le PDF
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Floating step indicator */}
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === step ? 'w-6 bg-red-500' : 'w-1.5 bg-zinc-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default InvoiceDemo;
